import api from './api';

// Classe para gerenciar autenticação
class AuthService {
  /**
   * Realiza login do usuário
   * @param {Object} credentials - Credenciais de login
   * @param {string} credentials.email - Email do usuário  
   * @param {string} credentials.password - Senha do usuário
   * @returns {Promise<Object>} Dados do usuário logado
   */
  async login(credentials) {
    try {
      const response = await api.post('/auth/login', credentials);
      
      const { accessToken, refreshToken, user } = response.data;
      
      // Armazenar os tokens no localStorage
      localStorage.setItem('token', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      
      return { token: accessToken, refreshToken, user };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Erro no login:', error.response?.data || error.message);
      }
      throw error;
    }
  }

  /**
   * Registra um novo usuário
   * @param {Object} userData - Dados do usuário
   * @returns {Promise<Object>} Dados do usuário registrado
   */
  async register(userData) {
    try {
      const response = await api.post('/auth/register', userData);
      
      const { accessToken, refreshToken, user } = response.data;
      
      // Armazenar os tokens no localStorage
      localStorage.setItem('token', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      
      return { token: accessToken, refreshToken, user };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Erro no registro:', error.response?.data || error.message);
      }
      throw error;
    }
  }

  /**
   * Realiza logout do usuário
   * @param {boolean} redirect - Se deve redirecionar para login
   */
  async logout(redirect = false) {
    try {
      // Tentar fazer logout no servidor
      await api.post('/auth/logout');
    } catch (error) {
      console.warn('Erro ao fazer logout no servidor:', error);
    } finally {
      // Sempre limpar dados locais
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      
      if (redirect) {
        window.location.href = '/login';
      }
    }
  }

  /**
   * Verifica se o usuário está autenticado com token válido
   * @returns {boolean} True se autenticado com token válido, false caso contrário
   */
  isAuthenticated() {
    const token = localStorage.getItem('token');
    if (!token) return false;
    
    // Verificar se o token é válido estruturalmente
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      
      // Verificar se o token não expirou
      return payload.exp > currentTime;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Token inválido detectado:', error.message);
      }
      return false;
    }
  }

  /**
   * Obtém o token atual
   * @returns {string|null} Token ou null se não existir
   */
  getToken() {
    return localStorage.getItem('token');
  }

  /**
   * Obtém os dados do usuário atual
   * @returns {Object|null} Dados do usuário ou null se não existir
   */
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Verifica se o usuário atual é administrador
   * @returns {boolean} True se for admin, false caso contrário
   */
  isAdmin() {
    const user = this.getCurrentUser();
    return user && (user.isAdmin === true || user.role === 'admin');
  }

  /**
   * Obtém o perfil completo do usuário do servidor
   * @returns {Promise<Object>} Dados do perfil do usuário
   */
  async getUserProfile() {
    try {
      const response = await api.get('/auth/profile');
      return response.data;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Erro ao buscar perfil:', error.response?.data || error.message);
      }
      throw error;
    }
  }

  /**
   * Atualiza os dados do usuário no localStorage
   * @param {Object} userData - Novos dados do usuário
   */
  updateUserData(userData) {
    localStorage.setItem('user', JSON.stringify(userData));
  }

  /**
   * Verifica se o token está válido fazendo uma requisição ao servidor
   * @returns {Promise<boolean>} True se válido, false caso contrário
   */
  async validateToken() {
    try {
      const response = await api.get('/auth/verify');
      return response.data.valid;
    } catch (error) {
      // Se o token for inválido, tentar refresh
      if (error.response?.status === 401 || error.response?.status === 403) {
        const refreshed = await this.refreshToken();
        if (!refreshed) {
          this.logout();
          return false;
        }
        return true;
      }
      throw error;
    }
  }

  /**
   * Atualiza o token usando o refresh token
   * @returns {Promise<boolean>} True se o refresh foi bem-sucedido
   */
  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        return false;
      }

      const response = await api.post('/auth/refresh', { refreshToken });
      const { accessToken, refreshToken: newRefreshToken } = response.data;

      // Atualizar tokens no localStorage
      localStorage.setItem('token', accessToken);
      localStorage.setItem('refreshToken', newRefreshToken);

      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Erro ao renovar token:', error.response?.data || error.message);
      }
      // Se falhar ao renovar, limpar tokens
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      return false;
    }
  }

  /**
   * Promove um usuário a administrador (apenas para admins)
   * @param {string} email - Email do usuário a ser promovido
   * @returns {Promise<Object>} Resultado da promoção
   */
  async promoteUserToAdmin(email) {
    try {
      const response = await api.post('/admin/promote', { email });
      return response.data;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Erro ao promover usuário:', error.response?.data || error.message);
      }
      throw error;
    }
  }
}

// Exportar uma instância singleton
const authService = new AuthService();
export default authService; 